import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { ToTarget } from '../../link_parser';
import { RCIcon } from '../RCIcon';
import Close from '../../../design-system/icons/core/close.svg?component';
import Plus from '../../../design-system/icons/core/plus.svg?component';

function PillText(props: { name: string, children?: React.ReactElement }) {
  const { name, children } = props;
  return (
    children || <span className="rc-pill__text">{name}</span>
  );
}

function PillIcon(props: { icon: string }) {
  const { icon } = props;
  if (!icon) {
    return null;
  }

  return (
    <RCIcon
      svgComponent={icon === 'remove' ? Close : Plus}
      title={I18n.t(`commons.pill.${icon}`)}
      inline
    />
  );
}

interface WrapperProps {
  /**
    A list of ITagsAttrs objects to render as RCPills
   */
  children: React.ReactElement<IRCPillProp> | React.ReactElement<IRCPillProp>[];
}

export function RCPills({ children }: WrapperProps) {
  return (
    <div className="rc-pills">
      {children}
    </div>
  );
}

interface IRCPillProp {
  /**
    When defined, this will display one of two icons: add or remove.
   */
  icon?: 'add' | 'remove' | '';
  /**
    The id of the ITagsAttrs object
   */
  id?: string;
  /**
    The text to render, will be ignored if children is defined
   */
  name?: string,
  /**
    If defined when no handleAction is defined, this will render a CoreLink. This is the path used for redirection.
   */
  to?: ToTarget, // define to create a CoreLink, will be ignored if handleAction is defined
  /**
    If defined, this will render a button. The function passed will be used when the button is clicked.
   */
  handleAction?: (tag: { id: string, name: string }) => void; // define to create a Button
  children?: React.ReactElement,
  /**
    If defined, this will render the html element instead of name, should be wrapped in a <span></span>
   */
}

export function RCPill(props: IRCPillProp) {
  const { handleAction, icon, to, id, name, children } = props;
  const noAction = !to && !handleAction;

  const pillClasses = classNames(
    'rc-pill',
    { 'rc-pill--with-icon': !!icon },
    { 'rc-pill--inactive': noAction },
  );

  if (handleAction) {
    return (
      <button
        type="button"
        className={pillClasses}
        onClick={() => handleAction({ id, name })}
      >
        <PillText name={name} children={children} />
        <PillIcon icon={icon} />
      </button>
    );
  }

  if (to) {
    return (
      <CoreLink
        to={to}
        className={pillClasses}
      >
        <PillText name={name} children={children} />
        <PillIcon icon={icon} />
      </CoreLink>
    );
  }

  return (
    <div className={pillClasses}>
      <PillText name={name} children={children} />
      <PillIcon icon={icon} />
    </div>
  );
}
